import { memo, useRef, useState } from 'react'
import { Eye, Loader2, Trash2, AlertTriangle } from 'lucide-react'
import { useHomeStore } from '../store'
import { fetchDocumentBlob } from '../api'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { CompanyValues, Vehicle } from '../types'
import { UZITI_VOZIDLA } from '../types'
import { CompanyAutocomplete } from './CompanyAutocomplete'

interface Props {
  vehicle: Vehicle
  onEditInsurance: () => void
  onDelete: () => void
  onVinChanged?: (id: number) => void
  // Whether the GLOBAL coverage has havarijní active (passed in so the card can
  // be memoised without subscribing to the store for it).
  globalHavActive?: boolean
}

function VehicleCardImpl({ vehicle, onEditInsurance, onDelete, onVinChanged, globalHavActive }: Props) {
  const updateVehicle = useHomeStore((s) => s.updateVehicle)
  const [docLoading, setDocLoading] = useState(false)
  const vinAtFocus = useRef<string | null>(null)
  const hasDoc = vehicle.source_doc_id !== undefined && vehicle.source_doc_id !== null
  const hasOverride = vehicle.insurance !== null && vehicle.insurance !== undefined

  // When the user corrects a misread VIN, drop the stale registry data and
  // re-fetch (the OCR'd value pulled the wrong vehicle). Triggered on blur so
  // we don't hit the registry on every keystroke.
  function handleVinBlur() {
    const vin = vehicle.vin.trim().toUpperCase()
    if (vin.length === 17 && vin !== vinAtFocus.current) {
      updateVehicle(vehicle.id, { car_gov_data: null, registry_not_found: false })
      onVinChanged?.(vehicle.id)
    }
  }

  async function openSourceDoc() {
    if (!hasDoc || docLoading) return
    // Open the tab synchronously (inside the click gesture) to dodge popup
    // blockers, then point it at the fetched blob once it arrives.
    const w = window.open('about:blank', '_blank')
    setDocLoading(true)
    try {
      const blob = await fetchDocumentBlob(vehicle.source_doc_id!)
      const url = URL.createObjectURL(blob)
      if (w) w.location.href = url
      else window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      if (w) w.close()
    } finally {
      setDocLoading(false)
    }
  }
  const gov = vehicle.car_gov_data?.Data as Record<string, unknown> | undefined
  const showHodnota = isHavarijniActive(vehicle.insurance) || !!globalHavActive
  const ownerSame = vehicle.ownerSameAsPolicyholder !== false
  const operatorSame = vehicle.operatorSameAsPolicyholder !== false

  return (
    <article
      // content-visibility lets the browser skip layout/paint of off-screen
      // cards, so large fleets (30+ vehicles) don't lock the main thread.
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 460px' }}
      className={cn(
        'rounded-lg border bg-white shadow-sm',
        vehicle.registry_not_found ? 'border-amber-200' : 'border-zinc-200',
      )}
    >
      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_1fr_340px]">
        <div className="space-y-3">
          <Field label={`VIN${vehicle.source ? ` (${vehicle.source})` : ''}`}>
            <Input
              value={vehicle.vin}
              onChange={(e) => updateVehicle(vehicle.id, { vin: e.target.value.toUpperCase() })}
              onFocus={() => { vinAtFocus.current = vehicle.vin }}
              onBlur={handleVinBlur}
              className="font-mono"
            />
          </Field>
          <Field label="Najeté km">
            <Input
              value={vehicle.najete_km ?? ''}
              onChange={(e) => updateVehicle(vehicle.id, { najete_km: e.target.value })}
              placeholder="Zadejte najeté kilometry"
              inputMode="numeric"
            />
          </Field>
          {showHodnota && (
            <Field label="Hodnota vozidla">
              <Input
                value={vehicle.hodnota_vozidla ?? ''}
                onChange={(e) => updateVehicle(vehicle.id, { hodnota_vozidla: e.target.value })}
                placeholder="Zadejte hodnotu vozidla"
                inputMode="numeric"
              />
            </Field>
          )}
          <Field label="Začátek pojištění">
            <Input
              type="date"
              value={vehicle.insurance_start_date ?? ''}
              onChange={(e) => updateVehicle(vehicle.id, { insurance_start_date: e.target.value })}
            />
          </Field>
          <Field label="Užití vozidla">
            <select
              value={vehicle.uziti_vozidla ?? UZITI_VOZIDLA[0]}
              onChange={(e) => updateVehicle(vehicle.id, { uziti_vozidla: e.target.value })}
              className="h-10 w-full rounded-md border border-zinc-300 bg-white px-2 text-xs"
            >
              {UZITI_VOZIDLA.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </Field>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-zinc-700">
              <Checkbox
                checked={ownerSame}
                onCheckedChange={(v) => updateVehicle(vehicle.id, { ownerSameAsPolicyholder: !!v, owner_company: v ? null : vehicle.owner_company, ownerOverridden: true })}
              />
              <span>Vlastník je shodný s pojistníkem</span>
            </label>
            {!ownerSame && (
              <CompanyAutocomplete
                value={vehicle.owner_company ?? {}}
                onChange={(c) => updateVehicle(vehicle.id, { owner_company: c as CompanyValues | null, ownerOverridden: true })}
                placeholder="IČO nebo název vlastníka"
                limit={5}
              />
            )}
            <label className="flex items-center gap-2 text-xs text-zinc-700">
              <Checkbox
                checked={operatorSame}
                onCheckedChange={(v) => updateVehicle(vehicle.id, { operatorSameAsPolicyholder: !!v, operator_company: v ? null : vehicle.operator_company, operatorOverridden: true })}
              />
              <span>Provozovatel je shodný s pojistníkem</span>
            </label>
            {!operatorSame && (
              <CompanyAutocomplete
                value={vehicle.operator_company ?? {}}
                onChange={(c) => updateVehicle(vehicle.id, { operator_company: c as CompanyValues | null, operatorOverridden: true })}
                placeholder="IČO nebo název provozovatele"
                limit={5}
              />
            )}
          </div>
        </div>

        <div className="space-y-3">
          <Field label="SPZ">
            <Input
              value={vehicle.spz ?? ''}
              onChange={(e) => updateVehicle(vehicle.id, { spz: e.target.value })}
              placeholder="Zadejte SPZ"
            />
          </Field>
          <Field label="Další informace">
            <textarea
              value={vehicle.dalsi_informace ?? ''}
              onChange={(e) => updateVehicle(vehicle.id, { dalsi_informace: e.target.value })}
              rows={5}
              className="w-full resize-none rounded-md border border-zinc-300 bg-white px-3 py-2 text-xs"
              placeholder="Zadejte další informace"
            />
          </Field>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEditInsurance}
              className={cn(
                'flex-1 rounded-md border px-3 py-2 text-left text-xs transition',
                hasOverride
                  ? 'border-purple-400 bg-purple-50 text-purple-800'
                  : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50',
              )}
            >
              {hasOverride ? 'Individuální pojistné krytí' : 'Globální pojistné krytí'}
            </button>
            <Button
              variant="ghost"
              size="icon"
              onClick={openSourceDoc}
              disabled={!hasDoc || docLoading}
              title={hasDoc ? 'Zobrazit zdrojový dokument' : 'Dokument není k dispozici'}
            >
              {docLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
              ) : (
                <Eye className={cn('h-4 w-4', hasDoc ? 'text-zinc-500' : 'text-zinc-300')} />
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete} title="Smazat vozidlo" className="bg-red-50 hover:bg-red-100">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>

        <GovDataPanel
          gov={gov}
          status={vehicle.car_gov_data?.Status}
          loading={!vehicle.car_gov_data && !vehicle.registry_not_found}
          notFound={!!vehicle.registry_not_found}
        />
      </div>
    </article>
  )
}

function GovDataPanel({
  gov,
  status,
  loading,
  notFound,
}: {
  gov?: Record<string, unknown>
  status?: number
  loading: boolean
  notFound: boolean
}) {
  if (loading) {
    return (
      <aside className="flex items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
        Načítám data z registru…
      </aside>
    )
  }
  if (!gov || Object.keys(gov).length === 0) {
    return (
      <aside className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>Vozidlo nenalezeno v centrálním registru.</span>
      </aside>
    )
  }

  const fromRegistry = status === 1 && !notFound
  const palette = fromRegistry
    ? {
        wrap: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        labelText: 'text-emerald-700',
        valueText: 'text-emerald-700 font-medium',
      }
    : {
        wrap: 'border-orange-200 bg-orange-50 text-orange-900',
        labelText: 'text-orange-700',
        valueText: 'text-orange-700 font-medium',
      }

  const rows: [string, string][] = [
    ['Druh', String(gov.VozidloDruh ?? '—').toUpperCase()],
    ['Specifikace', specifikaceLabel(gov)],
    ['Tovární značka', String(gov.TovarniZnacka ?? '—')],
    ['Obchodní označení', String(gov.ObchodniOznaceni ?? gov.Typ ?? '—')],
    ['1. registrace', formatDate(String(gov.DatumPrvniRegistrace ?? ''))],
    ['Motor', motor(gov)],
    ['Výkon (kW)', vykon(gov)],
    ['Max. hmotnost', maxHmotnost(gov)],
    ['Typ paliva', palivo(String(gov.Palivo ?? ''))],
    ['Počet míst', firstSegment(String(gov.VozidloKaroserieMist ?? ''))],
  ]
  return (
    <aside className={cn('rounded-md border p-4', palette.wrap)}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">Data vozidla</span>
        <span className="text-[10px] uppercase tracking-wide opacity-70">
          {fromRegistry ? 'z registru' : 'z dokumentu'}
        </span>
      </div>
      {!fromRegistry && (
        <div className="mb-3 flex items-start gap-2 rounded border border-yellow-300 bg-yellow-50 p-2 text-[11px] text-yellow-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>Vozidlo nebylo nalezeno v registru vozidel. Zobrazena jsou pouze data z dokumentu.</span>
        </div>
      )}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className={palette.labelText}>{k}:</dt>
            <dd className={cn('text-right truncate', palette.valueText)} title={v}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </aside>
  )
}

function specifikaceLabel(gov: Record<string, unknown>): string {
  return String(
    gov.Specifikace ||
      gov.VozidloDruh2R ||
      gov.VozidloDruh2 ||
      gov.DruhVozidla2R ||
      gov.DruhVozidla2 ||
      '—',
  )
}

function motor(gov: Record<string, unknown>): string {
  const v = gov.MotorZdvihObjem ?? gov.MotorObjem
  if (typeof v === 'number') return String(Math.round(v))
  if (typeof v === 'string' && v) return v
  return 'Neznámý'
}

function vykon(gov: Record<string, unknown>): string {
  const raw = String(gov.MotorMaxVykon ?? '')
  if (!raw) return 'Neznámý'
  const first = raw.split('/')[0]?.trim()
  return first || 'Neznámý'
}

/**
 * Největší technicky přípustná hmotnost (F.1). Registr posílá „F.1/ F.2",
 * starší data z dokladu „G/ F.1" s „0" místo neznámé provozní hmotnosti —
 * v obou je F.1 největší kladné číslo. 0 není hmotnost, ale „neznámo" (PETR-244:
 * Yamaha z COC ukazovala 0 místo 298).
 */
function maxHmotnost(gov: Record<string, unknown>): string {
  const raw = gov.HmotnostiPripPov
  const nums = (typeof raw === 'number' ? [raw] : (String(raw ?? '').match(/\d{2,6}/g) ?? []).map(Number)).filter(
    (n) => Number.isFinite(n) && n > 0,
  )
  if (nums.length) return String(Math.max(...nums))
  const provozni = gov.HmotnostiProvozni
  if (typeof provozni === 'number' && provozni > 0) return String(provozni)
  return 'Neznámý'
}

function palivo(code: string): string {
  const c = code.trim().toUpperCase()
  if (!c) return 'Neznámý'
  if (c.startsWith('BA')) return 'Benzín'
  if (c.startsWith('NM') || c.startsWith('NF')) return 'Nafta'
  if (c.startsWith('LPG')) return 'LPG'
  if (c.startsWith('CNG')) return 'CNG'
  if (c.startsWith('EL')) return 'Elektřina'
  if (c.startsWith('H')) return 'Hybrid'
  return code
}

function firstSegment(raw: string): string {
  if (!raw) return 'Neznámý'
  const first = raw.split('/')[0]?.trim()
  return first || 'Neznámý'
}

/** Doklad často nese jen rok (COC) nebo rok a měsíc — nevymýšlíme 01.01. */
function formatDate(iso: string): string {
  if (!iso) return '—'
  const full = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (full) return `${full[3]}.${full[2]}.${full[1]}`
  const ym = /^(\d{4})-(\d{2})$/.exec(iso)
  if (ym) return `${ym[2]}/${ym[1]}`
  return iso
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  )
}

export function isHavarijniActive(c?: Vehicle['insurance']): boolean {
  if (!c) return false
  const v = c['havarijni-rozsah']
  return !!v && v !== '-' && v !== 'Bez pojištění'
}

// Re-render a card only when its own vehicle (or the global havarijní flag that
// drives the "Hodnota vozidla" field) changes — not on every list update.
export const VehicleCard = memo(
  VehicleCardImpl,
  (a, b) => a.vehicle === b.vehicle && a.globalHavActive === b.globalHavActive,
)
